import { SignInState } from "../Components/signInState";
import { Alert, CreateAlertRequest } from "../Types/alert";

declare let $: any;

/**
 * Validates if a string is a valid GUID format.
 * Format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (8-4-4-4-12 hex characters)
 */
export const isValidGuid = (value: string): boolean => {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidRegex.test(value);
};

/**
 * Extracts the error message from the API error response.
 * Parses JSON responses to extract the most specific error message available.
 */
export const getErrorMessage = (errorText: string): string => {
  try {
    const parsed = JSON.parse(errorText);
    return parsed.error || parsed.message || errorText;
  } catch {
    return errorText;
  }
};

export const checkAccessTokenExpiry = async (accessToken: string) => {
  // Split the token into parts
  const payload = accessToken.split('.')[1];
  // Base64 decode the payload
  const decodedPayload = atob(payload);
  // Parse the decoded payload to get an object
  const payloadObj = JSON.parse(decodedPayload);
  // Get the expiration time from the payload
  const exp = payloadObj.exp;
  return exp;
}

const parseRequestBody = (init: any): any => {
  if (!init?.body) return {};
  
  try {
    return JSON.parse(init.body as string);
  } catch {
    return null; // Return null to indicate parsing failed
  }
};

const extractValueFromResponse = (responseData: any): number | undefined => {
  return responseData?.value?.Table?.DataColumns?.at(0)?.Values?.at(0);
};

const createAlertFromData = (
  reqBody: any, 
  value: number, 
  signInState: SignInState, 
  setCurrentAlert: (alert: Alert | CreateAlertRequest) => void
) => {
  if (Object.keys(reqBody).length === 0 || value === undefined) return;
  
  setCurrentAlert({
    AlertId: 0,
    AlertTitle: `${reqBody?.dashboardId} - ${reqBody?.selectedWidget?.Title}`,
    AlertDescription: reqBody?.selectedWidget?.Description || "",
    RevealBiPayload_JSON: JSON.stringify(reqBody),
    IsActive: true,
    IsPublic: true,
    NotificationChannels: "email",
    AlertSensitivity: "threshold-crossed",
    ComparisonOperator: ">",
    MetricName: reqBody?.selectedWidget?.Title,
    ThresholdValue: value,
    NotificationEmails: signInState.userInfo[0]?.Email || "",
  });
};

const handleDashboardDataRequest = async (
  input: any, 
  init: any, 
  originalFetch: typeof globalThis.fetch,
  signInState: SignInState,
  setCurrentAlert: (alert: Alert | CreateAlertRequest) => void
) => {
  const reqBody = parseRequestBody(init);
  if (reqBody === null) {
    alert("We weren't able to read the visualization data. Please try again.");
    return originalFetch(input, init);
  }

  const response = await originalFetch(input, init);
  const clonedResponse = response.clone();
  const responseData = await clonedResponse.json().catch(() => clonedResponse.text());
  
  const value = extractValueFromResponse(responseData);
  if (value !== undefined) {
    createAlertFromData(reqBody, value, signInState, setCurrentAlert);
  }
  
  return response;
};

const createFetchInterceptor = (
  signInState: SignInState,
  setCurrentAlert: (alert: Alert | CreateAlertRequest) => void
) => {
  const originalFetch = globalThis.fetch;
  
  return async (input: any, init: any) => {
    let url: string;
    if (typeof input === "string") {
      url = input;
    } else if (input instanceof Request) {
      url = input.url;
    } else {
      url = input.toString();
    }

    if (url.includes("analysis/dashboard/editor/widget/data")) {
      const response = await handleDashboardDataRequest(input, init, originalFetch, signInState, setCurrentAlert);
      globalThis.fetch = originalFetch;
      return response;
    }

    globalThis.fetch = originalFetch;
    return originalFetch(input, init);
  };
};

export const addAlertMenuItemToVisualization = (revealView: any, signInState: SignInState, setCurrentAlert: (alert: Alert | CreateAlertRequest) => void) => {
  // Check if Alerts feature is enabled for this customer
  if (signInState.featureFlags) {
    const alertsFeature = signInState.featureFlags.find(
      (flag: any) => flag.FeatureName === 'Alerts'
    );
    if (!alertsFeature || !alertsFeature.IsEnabled) {
      return;
    }
  }

  const existingOnMenuOpening = revealView.onMenuOpening;

  revealView.onMenuOpening = function (visualization: any, args: any) {
    if (typeof existingOnMenuOpening === 'function') {
      existingOnMenuOpening(visualization, args);
    }

    if (visualization?.chartType !== 'LabelGauge') return;
    
    const refreshAction = args?.menuItems?.find((item: any) => item.title === "Refresh")?.action;
    const menuItem = new $.ig.RVMenuItem(
      "Create Alert",
      new $.ig.RVImage("/alerts_18122670.png", "Icon"),
      () => {
        globalThis.fetch = createFetchInterceptor(signInState, setCurrentAlert);
        refreshAction?.();
      }
    );
    args?.menuItems?.push(menuItem);
  }
}